import React, { useState, useRef, useEffect } from 'react';
import { ImagePlus } from 'lucide-react';
import adminImageStyleService from '../../services/api/adminImageStyleService';
import { TaskyButton, TaskyLabel, TaskySpinner } from '../tasky';

export interface ImageUploadApi {
  uploadImage: (file: File, type: 'reference' | 'thumbnail') => Promise<{ path: string; url: string }>;
  getImageUrl: (path: string) => Promise<string>;
}

interface ImageUploadProps {
  label: string;
  type: 'reference' | 'thumbnail';
  value: string;
  onChange: (path: string) => void;
  disabled?: boolean;
  helpText?: string;
  api?: ImageUploadApi;
  pathPrefix?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  type,
  value,
  onChange,
  disabled = false,
  helpText,
  api,
  pathPrefix = 'image-styles/',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load preview for existing image path
  useEffect(() => {
    const loadPreview = async () => {
      const getUrl = api?.getImageUrl ?? adminImageStyleService.getImageUrl;
      if (value && value.startsWith(pathPrefix)) {
        try {
          const url = await getUrl(value);
          setPreviewUrl(url);
        } catch (err) {
          console.error('Failed to load image preview:', err);
          setPreviewUrl(null);
        }
      } else if (value && (value.startsWith('http://') || value.startsWith('https://'))) {
        setPreviewUrl(value);
      } else {
        setPreviewUrl(null);
      }
    };

    loadPreview();
  }, [value]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const upload = api?.uploadImage ?? adminImageStyleService.uploadImage;
      const result = await upload(file, type);
      onChange(result.path);

      // Create local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('画像のアップロードに失敗しました');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-1.5">
      <TaskyLabel className="mb-0">{label}</TaskyLabel>

      <div>
        {previewUrl ? (
          <div className="inline-flex flex-col gap-2">
            <img
              src={previewUrl}
              alt="Preview"
              className="max-h-40 max-w-full rounded-tasky-md border border-warm-border bg-warm-surface object-contain"
            />
            <div className="flex items-center gap-2">
              <TaskyButton
                type="button"
                variant="outline"
                size="sm"
                onClick={handleButtonClick}
                disabled={disabled || isUploading}
              >
                変更
              </TaskyButton>
              <TaskyButton
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleRemove}
                disabled={disabled || isUploading}
              >
                削除
              </TaskyButton>
            </div>
          </div>
        ) : (
          <div
            className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-tasky-md border-2 border-dashed border-warm-border bg-warm-bg p-6 text-center transition-colors hover:border-primary/40 hover:bg-warm-hover"
            onClick={handleButtonClick}
          >
            {isUploading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <TaskySpinner size="sm" />
                アップロード中...
              </div>
            ) : (
              <>
                <ImagePlus className="h-8 w-8 text-gray-400" aria-hidden />
                <span className="text-sm font-medium text-dark">クリックして画像を選択</span>
                <span className="text-xs text-gray-500">PNG, JPG, GIF (5MB以下)</span>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {value && (
        <p className="break-all text-xs text-gray-500">
          パス: {value}
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
